import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, XCircle, Scale } from "lucide-react";

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const lastUpdated = "March 5, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Agreement */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-violet-100 rounded-lg">
                <FileText className="w-6 h-6 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Agreement to Terms</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using MarkeeMix ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service. These terms apply to all users, including visitors, registered users, and paying subscribers.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Description</h2>
            <p className="text-gray-600 leading-relaxed mb-2">
              MarkeeMix provides digital marketing tools including:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>SEO analysis and audit tools</li>
              <li>Keyword research and tracking</li>
              <li>Content marketing tools (AI writing, plagiarism checker)</li>
              <li>Social media management tools</li>
              <li>YouTube SEO tools</li>
              <li>PDF tools and utilities</li>
              <li>Chrome browser extension</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Accounts</h2>
            <p className="text-gray-600 leading-relaxed">
              When you create an account, you must provide accurate and complete information. You are responsible for:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Keeping your account information up to date</li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Acceptable Use</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-2">You agree to use our Service only for lawful purposes:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Analyze websites you own or have permission to analyze</li>
              <li>Generate content for legitimate business purposes</li>
              <li>Use tools within the limits of your subscription plan</li>
              <li>Respect intellectual property rights</li>
            </ul>
          </section>

          {/* Prohibited Activities */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Prohibited Activities</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-2">You may NOT:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Use automated scripts to access our Service excessively</li>
              <li>Attempt to bypass usage limits or security measures</li>
              <li>Reverse engineer or copy our software</li>
              <li>Use our Service for spamming or malicious purposes</li>
              <li>Resell or redistribute our Service without permission</li>
              <li>Upload malware or harmful content</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          {/* Subscription & Payments */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription & Payments</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Free Plan:</strong> Limited features with daily usage limits</li>
              <li><strong>Pro Plan:</strong> Full access with monthly/annual billing</li>
              <li>Payments are processed securely via Stripe</li>
              <li>Subscriptions auto-renew unless cancelled</li>
              <li>Refunds are handled on a case-by-case basis</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content, features, and functionality of MarkeeMix are owned by us and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute our content without permission.
            </p>
            <p className="text-gray-600 leading-relaxed mt-2">
              Content you create using our tools (blogs, reports, etc.) belongs to you. You grant us a limited license to display and process this content to provide our Service.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Disclaimer</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Our Service is provided "as is" without warranties of any kind. We do not guarantee:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li>Specific SEO results or rankings</li>
              <li>100% accuracy of analysis data</li>
              <li>Uninterrupted or error-free service</li>
              <li>That our tools will meet all your requirements</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Scale className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Limitation of Liability</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, MarkeeMix shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our Service. Our total liability shall not exceed the amount you paid us in the past 12 months.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              We may suspend or terminate your account if you violate these terms. You may cancel your account at any time. Upon termination, your right to use the Service will cease immediately. Some provisions of these terms will survive termination.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by the laws of India. Any disputes shall be resolved in the courts of Indore, Madhya Pradesh, India.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or website notice. Continued use of the Service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> contact@markeemix.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> +91 8269911081</p>
              <p className="text-gray-700"><strong>Address:</strong> Indore, MP, India</p>
            </div>
          </section>

        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <Button onClick={() => navigate("/")} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
